import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageSEO } from "@/components/SEO/PageSEO";
import { generateFAQSchema } from "@/utils/structuredData";
import "../styles/legal.css";
import "../styles/faq.css";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqData: FAQItem[] = [
    // Services & Solutions
    {
      category: "services",
      question: "What services does AI Genius Automations offer?",
      answer:
        "We provide custom AI-powered automation solutions including ERP system integration, workflow automation, data analytics, and intelligent business process optimization. Our solutions are tailored to eliminate operational friction and scale revenue without sacrificing margins.",
    },
    {
      category: "services",
      question: "How is your pricing structured?",
      answer:
        "We use milestone-based pricing, not never-ending retainers. Each project is broken into clear deliverables with defined timelines and fixed costs. You pay for completed milestones, ensuring zero surprises and complete transparency.",
    },
    {
      category: "services",
      question: "What industries do you serve?",
      answer:
        "We serve 10+ industries including automotive, manufacturing, healthcare, retail, professional services, and more. Our AI solutions are adaptable to any business with complex operational workflows and fragmented systems.",
    },

    // Implementation & Process
    {
      category: "implementation",
      question: "How long does a typical implementation take?",
      answer:
        "Implementation timelines vary by project scope, but most projects range from 6-16 weeks. We use our Strategic Process Transformation methodology to ensure smooth deployment with minimal disruption to your operations. Each milestone is clearly defined with specific deliverables and timelines.",
    },
    {
      category: "implementation",
      question: "What is the onboarding process?",
      answer:
        "Our engagement process includes: (1) Discovery - understanding your workflows and pain points, (2) Design - creating a custom solution blueprint, (3) Development - building and testing your system, (4) Deployment - smooth implementation with training, (5) Support - ongoing optimization and assistance.",
    },
    {
      category: "implementation",
      question: "Do you provide training for our team?",
      answer:
        "Yes! Comprehensive training is included with every implementation. We provide hands-on training sessions, detailed documentation, video tutorials, and ongoing support to ensure your team can effectively use and maintain the automated systems.",
    },
    {
      category: "implementation",
      question: "Will implementation disrupt our current operations?",
      answer:
        "We minimize disruption through careful planning and phased rollouts. Our implementations are designed to run parallel to existing systems initially, allowing for thorough testing before full transition. Most clients experience minimal operational impact.",
    },

    // Technical & Integration
    {
      category: "technical",
      question: "What systems can you integrate with?",
      answer:
        "We integrate with virtually any business system including ERP platforms (SAP, Oracle, Microsoft Dynamics), CRM systems (Salesforce, HubSpot), accounting software (QuickBooks, Xero), and custom databases. If it has an API or database, we can connect it.",
    },
    {
      category: "technical",
      question: "Do I need to replace my existing systems?",
      answer:
        "No! Our approach is to enhance and connect your existing systems rather than replace them. We build integration layers that allow your current systems to communicate seamlessly, eliminating manual data entry and reducing errors.",
    },
    {
      category: "technical",
      question: "Is my data secure?",
      answer:
        "Absolutely. We implement enterprise-grade security including AES-256 encryption, secure API connections, role-based access controls, and SOC 2 compliance standards. Your data never leaves your infrastructure without proper encryption and authorization.",
    },
    {
      category: "technical",
      question: "What happens if we need to scale or modify the system later?",
      answer:
        "Our solutions are built for scalability and flexibility. As your business grows or changes, we can easily add new integrations, modify workflows, and enhance functionality. You own the code and documentation, giving you complete control.",
    },

    // ROI & Results
    {
      category: "roi",
      question: "What kind of ROI can we expect?",
      answer:
        "Our clients typically see 3-5x ROI within the first year through reduced manual labor costs, eliminated errors, faster processing times, and improved decision-making. Specific results vary by implementation scope, but most clients recover their investment within 6-12 months.",
    },
    {
      category: "roi",
      question: "How do you measure success?",
      answer:
        "We establish clear KPIs before implementation including time savings, error reduction, process efficiency gains, and cost savings. We provide regular reporting dashboards and quarterly business reviews to track progress and optimize performance.",
    },
    {
      category: "roi",
      question: "What results have your clients achieved?",
      answer:
        "Our clients have achieved significant measurable outcomes: 68% reduction in operational costs, 40-60% time savings on manual processes, 95% reduction in data entry errors, and revenue growth without proportional overhead increases. Results vary by industry and implementation scope.",
    },

    // Support & Maintenance
    {
      category: "support",
      question: "What kind of ongoing support do you provide?",
      answer:
        "We offer multiple support tiers including email/chat support, priority phone support, regular system health checks, proactive optimization recommendations, and emergency troubleshooting. Support can be included in your implementation or purchased separately.",
    },
    {
      category: "support",
      question: "Can we maintain the system ourselves?",
      answer:
        "Yes! All of our solutions include comprehensive documentation, and you receive full access to the source code and infrastructure. We provide training for your technical team, though many clients prefer our ongoing maintenance packages for peace of mind.",
    },
    {
      category: "support",
      question: "What if something breaks?",
      answer:
        "Our systems include built-in monitoring, error detection, and automated alerts. Most issues are caught and resolved before they impact your operations. For critical systems, we offer 24/7 emergency support with guaranteed response times.",
    },

    // Getting Started
    {
      category: "getting-started",
      question: "How do I know if automation is right for my business?",
      answer:
        "If you're experiencing any of these: staff spending hours on manual data entry, information trapped in spreadsheets, delays in getting reports, errors from re-entering data across systems, or difficulty scaling without adding headcount—you're an excellent candidate for automation.",
    },
    {
      category: "getting-started",
      question: "What's the first step to get started?",
      answer:
        "Book a free 15-minute strategy call through our website. We'll discuss your current challenges, identify quick wins, and determine if automation is right for you. No sales pressure—just honest advice about whether we can help.",
    },
    {
      category: "getting-started",
      question: "Do you offer consulting without implementation?",
      answer:
        "Yes! We offer strategic consulting engagements to help you assess automation opportunities, create implementation roadmaps, and provide technical guidance. Some clients start with consulting before committing to full implementation.",
    },
    {
      category: "getting-started",
      question: "What information do you need to provide a quote?",
      answer:
        "To provide an accurate quote, we need to understand: (1) your current systems and workflows, (2) specific pain points and goals, (3) desired timeline, and (4) any technical constraints. We typically schedule a discovery call to gather this information and provide a detailed proposal within 3-5 business days.",
    },
  ];

  const categories = [
    { id: "all", label: "All Questions" },
    { id: "services", label: "Services & Solutions" },
    { id: "implementation", label: "Implementation & Process" },
    { id: "technical", label: "Technical & Integration" },
    { id: "roi", label: "ROI & Results" },
    { id: "support", label: "Support & Maintenance" },
    { id: "getting-started", label: "Getting Started" },
  ];

  const filteredFAQs =
    activeCategory === "all"
      ? faqData
      : faqData.filter((faq) => faq.category === activeCategory);

  const toggleQuestion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Generate FAQ structured data from our FAQ items
  const faqSchema = generateFAQSchema(
    faqData.map((item) => ({
      question: item.question,
      answer: item.answer,
    })),
  );

  return (
    <>
      <PageSEO
        title="Frequently Asked Questions"
        description="Get answers to common questions about AI Genius Automations' AI-powered automation services, implementation process, pricing, ROI, and how we help businesses break the profitability paradox."
        keywords="AI automation FAQ, automation questions, ERP integration help, workflow automation support, business automation guide, AI implementation process, automation pricing, ROI calculator questions"
        ogImage="/og-image-faq.png"
        structuredData={faqSchema}
      />

      <div className="legal-page">
        <div className="legal-container">
          <button
            onClick={() => navigate(-1)}
            className="legal-back-button"
            aria-label="Go back"
          >
            ← Back
          </button>

          <h1 className="legal-title">Frequently Asked Questions</h1>

          <div className="legal-meta">
            <span>
              Get answers to common questions about our AI automation services
            </span>
          </div>

          {/* Category Filter */}
          <section className="legal-section">
            <div className="faq-categories">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setActiveIndex(null);
                  }}
                  className={`faq-category-button ${activeCategory === category.id ? "active" : ""}`}
                  aria-pressed={activeCategory === category.id}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </section>

          {/* FAQ Items */}
          <section className="legal-section">
            <div className="faq-list">
              {filteredFAQs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${activeIndex === index ? "active" : ""}`}
                >
                  <button
                    onClick={() => toggleQuestion(index)}
                    className="faq-question"
                    aria-expanded={activeIndex === index}
                  >
                    <span>{faq.question}</span>
                    <svg
                      className={`faq-icon ${activeIndex === index ? "rotate" : ""}`}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {activeIndex === index && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="legal-section">
            <div className="faq-cta">
              <h2>Still have questions?</h2>
              <p>
                We're here to help! Schedule a free 15-minute strategy call to
                discuss your specific needs and how we can help automate your
                business processes.
              </p>
              <a
                href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
                target="_blank"
                rel="noopener noreferrer"
                className="faq-cta-button"
              >
                Book a Strategy Call
              </a>
              <p className="faq-cta-secondary">
                Or email us at{" "}
                <a href="mailto:noah@aigeniusautomations.com">
                  noah@aigeniusautomations.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default FAQ;
